import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {siste} from "felles-ng2/støttefunksjoner/lister";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {nil} from "felles-ng2/typer/nullish";


export type RedigerListeRadKontroller<T> =
    { [K in keyof T]: AbstractControl<any> } & {
        tittel: FormControl<string>;
        slettet: FormControl<boolean>;
        ny: FormControl<boolean>;
    };


/**
 * Vedlikeholder et FormArray av rader for "rediger-liste"-komponenter:
 * - sørger for at det alltid finnes en tom 'ny' rad sist
 * - fjerner mellomliggende tomme 'ny' rader
 * - markerer rader med duplikate titler med valideringsfeilen { duplikat: true }
 *
 * Skjemaet må inneholde feltene `tittel`, `slettet` og `ny`.
 */
export function håndterRedigerListeEndringer<TKontroller extends RedigerListeRadKontroller<TKontroller>>(
    skjema: FormArray<FormGroup<TKontroller>>,
    opprettTomRad: () => FormGroup<TKontroller>
): void {
    const verdier = skjema.value;

    //sørger for at det alltid finnes en tom 'nytt innslag' rad til slutt
    if (erIkkeTomRadEllerNull(siste(verdier)!, erTomRad)) {
        skjema.controls.filter(rad => rad.value.ny)
            .forEach(rad => rad.controls.tittel.addValidators(Validators.required));
        skjema.push(opprettTomRad());
    }

    //slett raden dersom den er ny og tittel blankes ut
    verdier.forEach((verdi, indeks) => {
        if (erTomRad(verdi) && verdi.ny && indeks < verdier.length - 1)
            skjema.removeAt(indeks);
    });

    //vis feilmelding ved duplikate titler
    const unikeTitler = new Set<string>();
    const aktuelleRader = skjema.controls
      //ignorer siste rad (tom 'nytt innslag' rad)
        .filter((_, indeks) => indeks < skjema.controls.length - 1)
      //ignorer rader som skal slettes
        .filter(rad => !rad.value.slettet);

    aktuelleRader.forEach(rad => {
        if (erTomEllerNil(rad.controls.tittel.value)) return;
        const tittelNormalisert = rad.value.tittel!.toLowerCase();
        if (unikeTitler.has(tittelNormalisert)) {
            //legg valideringsfeilen til alle radene som har duplikat tittel - må gjøres da vi her ikke vet hvilken av de aktuelle radene bruker er i ferd med å redigere
            aktuelleRader
              .filter(annenRad => annenRad.value.tittel!.toLowerCase() === tittelNormalisert)
              .forEach(annenRad => annenRad.controls.tittel.setErrors({duplikat: true}));
        } else {
            const feil = rad.controls.tittel.errors;
            if (feil != null) {
                delete feil['duplikat'];
                rad.controls.tittel.setErrors(Object.keys(feil).length ? feil : null);
            }
        }
        unikeTitler.add(tittelNormalisert);
    });
}

function erTomRad<T extends RedigerListeRadKontroller<T>>(verdier: FormGroup<T>['value']): boolean {
    return erTomEllerNil(verdier.tittel);
}

function erIkkeTomRadEllerNull<T extends RedigerListeRadKontroller<T>>(
    rad: FormGroup<T>['value'] | nil,
    erTomRad: (verdier: FormGroup<T>['value']) => boolean,
): boolean {
    if (erNil(rad)) return true;
    return !erTomRad(rad);
}
